import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { documents, type DocumentOwnerType } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { ajudaDoRepositorio } from '@/shared/help/ajuda/documents';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { enviarArquivo, formatBytes, recusaDoArquivo } from './envio';
import { chaveDeEscrita } from './escritaDoDono';
import { UploadDropzone } from './UploadDropzone';

/**
 * "Upload de documento" do OnPoint: nomear e enviar no mesmo dialogo, com arrastar e
 * soltar.
 *
 * Por baixo sao os dois passos do repositorio, registrar o documento e enviar o
 * arquivo. O documento nasce opcional, no grupo informado. Se o registro nasce e o
 * envio falha, o documento aparece pendente na lista, e o arquivo pode ser enviado
 * por ali.
 *
 * O nome nao e obrigatorio no OnPoint (44, secao 12.1): vazio, o documento leva o
 * nome do arquivo, sem a extensao.
 */
export function UploadDocumentDialog({
  ownerType,
  ownerId,
  group,
  onSent,
}: {
  ownerType: DocumentOwnerType;
  /** Pode faltar no documento da seguradora: o dono e a propria empresa. */
  ownerId?: string | undefined;
  group: string;
  onSent?: (() => void) | undefined;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [nome, setNome] = useState('');
  const [comentario, setComentario] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);

  const podeEnviar = can(chaveDeEscrita(ownerType, 'documentos.documento.create'));
  const armazenamento = useQuery({
    queryKey: ['document-storage'],
    queryFn: ({ signal }) => documents.storage(signal),
    staleTime: 5 * 60 * 1000,
    enabled: podeEnviar,
  });

  function fechar() {
    setAberto(false);
    setNome('');
    setComentario('');
    setArquivo(null);
  }

  const enviar = useMutation({
    mutationFn: async () => {
      // O uso da empresa e lido de novo antes de conferir a cota: a leitura guardada
      // pode ter minutos, e o registro do documento so nasce se o arquivo couber.
      const storage = (await armazenamento.refetch()).data ?? armazenamento.data;
      if (!storage || !arquivo) throw new Error('Escolha o arquivo.');
      const recusa = recusaDoArquivo(arquivo, storage);
      if (recusa) throw new Error(recusa);
      const titulo = nome.trim() || arquivo.name.replace(/\.[^.]+$/, '');
      const registro = await documents.require({
        owner_type: ownerType,
        ...(ownerId ? { owner_id: ownerId } : {}),
        group,
        title: titulo,
        required: false,
      });
      const observacao = comentario.trim();
      return observacao
        ? enviarArquivo(registro.id, arquivo, storage, { comment: observacao })
        : enviarArquivo(registro.id, arquivo, storage);
    },
    onSuccess: (resultado) => {
      toast.success('Documento enviado.');
      if (resultado.avisoDeCota) toast.info(resultado.avisoDeCota);
      fechar();
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      void queryClient.invalidateQueries({ queryKey: ['document-storage'] });
      onSent?.();
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });

  if (!podeEnviar) return null;
  const storage = armazenamento.data;
  if (storage && !storage.available) {
    return (
      <Button
        size="sm"
        variant="secondary"
        disabled
        title={`Envio de arquivo indisponivel nesta instalacao. ${storage.missing_requirement}`}
      >
        Upload de documento
      </Button>
    );
  }
  const tipos = storage ? Object.values(storage.accepted_types).join(', ') : '';

  return (
    <>
      <Button size="sm" onClick={() => setAberto(true)} disabled={!storage}>
        Upload de documento
      </Button>
      <Modal
        open={aberto}
        onClose={fechar}
        title="Upload de documento"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={fechar}>
              Cancelar
            </Button>
            <Button
              loading={enviar.isPending}
              disabled={arquivo === null}
              onClick={() => enviar.mutate()}
            >
              Enviar documento
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {enviar.error ? (
            <Alert tone="danger" title="Nao foi possivel enviar">
              {enviar.error.message}
            </Alert>
          ) : null}
          <Field
            label="Nomear documento"
            value={nome}
            placeholder="Digite o nome do documento"
            help={ajudaDoRepositorio['Nomear documento']}
            onChange={(event) => setNome(event.target.value)}
          />
          <UploadDropzone
            arquivo={arquivo}
            onArquivo={setArquivo}
            accept={storage ? Object.keys(storage.accepted_types).join(',') : undefined}
          />
          {storage ? (
            <p className="text-2xs text-ink-500">
              Somente arquivos do tipo {tipos}, ate {formatBytes(storage.max_bytes)}.
            </p>
          ) : null}
          <Textarea
            label="Comentario do envio"
            value={comentario}
            rows={2}
            maxLength={2000}
            help={ajudaDoRepositorio['Comentario do envio']}
            onChange={(event) => setComentario(event.target.value)}
          />
        </div>
      </Modal>
    </>
  );
}
